import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AssessmentIcon from "@material-ui/icons/Assessment";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import CancelScheduleSendIcon from "@material-ui/icons/CancelScheduleSend";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import StarIcon from "@material-ui/icons/Star";
import TimerIcon from "@material-ui/icons/Timer";
import EventIcon from "@material-ui/icons/Event";
import VisibilityIcon from "@material-ui/icons/Visibility";
import managerTestApi from "api/managerTest";
import { ListExam, ListTest, ListTestByCodeExamResponse } from "models/managerTest";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ListTestByCodeExam from "../ListTestByCodeExam";
import ListSkipExam from "../ListSkipExam";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: 4,
    paddingBottom: 24,
    maxWidth: 1200,
    margin: "0 auto",
    [theme.breakpoints.down("md")]: {
      paddingLeft: 8,
      paddingRight: 8,
      paddingTop: 0,
      paddingBottom: 16,
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: "0px !important",
      paddingRight: "0px !important",
      paddingTop: 0,
      paddingBottom: 12,
    },
  },
  backButton: {
    color: "#475569",
    fontWeight: 700,
    fontSize: "0.85rem",
    textTransform: "none",
    marginBottom: 8,
    borderRadius: 8,
    padding: "3px 10px",
    backgroundColor: "#F1F5F9",
    border: "1px solid #E2E8F0",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#E2E8F0",
      color: "#0F172A",
    },
  },
  headerBanner: {
    background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 40%, #312E81 70%, #4338CA 100%)",
    borderRadius: 18,
    padding: "20px 24px",
    color: "#FFFFFF",
    marginBottom: 16,
    boxShadow: "0 10px 24px -8px rgba(30, 27, 75, 0.35)",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 14px",
      borderRadius: 14,
      marginBottom: 12,
    },
  },
  headerGlow: {
    position: "absolute",
    top: -80,
    right: -80,
    width: 280,
    height: 280,
    background: "radial-gradient(circle, rgba(129,140,248,0.35) 0%, rgba(0,0,0,0) 70%)",
    pointerEvents: "none",
  },
  headerBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 14px",
    borderRadius: 20,
    backgroundColor: "rgba(99, 102, 241, 0.3)",
    border: "1px solid rgba(165, 180, 252, 0.4)",
    color: "#C7D2FE",
    fontWeight: 800,
    fontSize: "0.75rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  headerTitle: {
    fontSize: "1.85rem",
    fontWeight: 800,
    marginBottom: 12,
    letterSpacing: "-0.02em",
    color: "#FFFFFF",
    wordBreak: "break-word",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
      marginBottom: 8,
    },
  },
  headerMeta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  metaPill: {
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    backdropFilter: "blur(12px)",
    color: "#F8FAFC",
    fontWeight: 600,
    fontSize: "0.85rem",
    borderRadius: 10,
    height: 32,
    border: "1px solid rgba(255, 255, 255, 0.15)",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      height: 28,
    },
  },
  actionBtn: {
    backgroundColor: "rgba(255, 255, 255, 0.18)",
    backdropFilter: "blur(12px)",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "0.85rem",
    borderRadius: 10,
    padding: "6px 16px",
    textTransform: "none",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginTop: 8,
      justifyContent: "center",
    },
  },
  kpiGrid: {
    marginBottom: 16,
    "& .MuiGrid-item": {
      display: "flex",
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 10,
    },
  },
  kpiCard: {
    padding: 20,
    borderRadius: 20,
    backgroundColor: "#FFFFFF",
    border: "1px solid #E2E8F0",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.03)",
    display: "flex",
    alignItems: "center",
    gap: 16,
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    transition: "all 0.25s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      boxShadow: "0 12px 24px -6px rgba(0, 0, 0, 0.08)",
      borderColor: "#CBD5E1",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "8px 10px",
      borderRadius: 12,
      gap: 8,
    },
  },
  kpiIconBox: {
    width: 56,
    height: 56,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: 38,
      height: 38,
      borderRadius: 10,
    },
  },
  kpiValue: {
    fontSize: "1.6rem",
    fontWeight: 800,
    color: "#0F172A",
    lineHeight: 1.2,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2rem",
    },
  },
  kpiLabel: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#64748B",
    marginTop: 2,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
    },
  },
  tabsPaper: {
    borderRadius: 20,
    backgroundColor: "#FFFFFF",
    border: "1px solid #E2E8F0",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.03)",
    marginBottom: 12,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 14,
    },
  },
  tabsHeader: {
    borderBottom: "1px solid #E2E8F0",
    backgroundColor: "#F8FAFC",
    padding: "6px 20px 0 20px",
    [theme.breakpoints.down("sm")]: {
      padding: "4px 4px 0 4px",
    },
  },
  tabItem: {
    fontWeight: 700,
    fontSize: "0.95rem",
    textTransform: "none",
    minHeight: 52,
    color: "#64748B",
    padding: "12px 20px",
    "&.Mui-selected": {
      color: "#4F46E5",
    },
    [theme.breakpoints.down("sm")]: {
      minHeight: 44,
      padding: "6px 2px",
      minWidth: "0 !important",
      fontSize: "0.775rem",
    },
  },
  countBadge: {
    marginLeft: 8,
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: "0.75rem",
    fontWeight: 800,
    [theme.breakpoints.down("sm")]: {
      marginLeft: 4,
      padding: "1px 5px",
      fontSize: "0.7rem",
    },
  },
  tabContent: {
    paddingTop: 0,
  },
}));

export default function ExamResults() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const params = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);
  const [detailExam, setDetailExam] = useState<ListExam>();
  const [testSummary, setTestSummary] = useState<ListTestByCodeExamResponse<ListTest>>();
  const [assignedCount, setAssignedCount] = useState<number>(0);
  const [skippedCount, setSkippedCount] = useState<number>(0);
  const [overviewApiData, setOverviewApiData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, [loading]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const examId = Number(params.id);
        const [resOverview, resExam, resSummary, resUsers, resSkip] = await Promise.all([
          managerTestApi.getExamResultsOverview(examId).catch(() => undefined),
          managerTestApi.getDetailExam(examId).catch(() => undefined),
          managerTestApi.getListTest(examId, { page: 0, size: 9999 } as any).catch(() => undefined),
          managerTestApi.getUsersByExamID(String(examId)).catch(() => undefined),
          managerTestApi.getListSkipExam({ exam_id: String(examId), page_index: 0, page_size: 9999 } as any).catch(() => undefined),
        ]);
        if (resOverview?.responseCode === "00") {
          setOverviewApiData(resOverview);
        }
        setDetailExam(resExam);
        setTestSummary(resSummary);
        if ((resUsers as any)?.userList) {
          setAssignedCount((resUsers as any).userList.length);
        }
        if ((resSkip as any)?.dropExamList) {
          setSkippedCount((resSkip as any).dropExamList.length);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [params.id]);

  const examInfo = detailExam?.exam;
  const submittedList: ListTest[] = testSummary?.submissionDetails?.content || [];
  
  const totalSubmitted = overviewApiData?.totalSubmitted ?? (testSummary?.submissionDetails?.totalElements ?? submittedList.length);
  const totalAssigned = overviewApiData?.totalAssigned ?? (assignedCount || examInfo?.numberStudent || 0);
  const totalSkipped = overviewApiData?.totalSkipped ?? (skippedCount || Math.max(0, totalAssigned - totalSubmitted));

  const avgScore =
    overviewApiData?.avgScore !== undefined && overviewApiData?.avgScore !== null
      ? String(overviewApiData.avgScore)
      : submittedList.length > 0
      ? (
          submittedList.reduce((acc: number, curr: ListTest) => acc + (curr.point || 0), 0) /
          submittedList.length
        ).toFixed(1)
      : "--";

  const submissionRate =
    overviewApiData?.submissionRate !== undefined && overviewApiData?.submissionRate !== null
      ? String(overviewApiData.submissionRate)
      : totalAssigned > 0
      ? ((totalSubmitted / totalAssigned) * 100).toFixed(1)
      : "0";

  const isEssay =
    examInfo?.type?.toUpperCase() === "TYPING" ||
    examInfo?.type?.toUpperCase() === "ESSAY";

  return (
    <div ref={containerRef}>
      <Container maxWidth="lg" className={classes.container}>
        {/* Back Button */}
        <Button
          onClick={() => navigate("/admin/manager-test/list")}
          className={classes.backButton}
          startIcon={<ArrowBackIcon />}
        >
          {t("button.goback") || "Quay lại danh sách bài thi"}
        </Button>

        {/* Header Banner */}
        <Box className={classes.headerBanner}>
          <Box className={classes.headerGlow} />
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 16 }}>
            <Box>
              <Box className={classes.headerBadge}>
                {isEssay ? "TỰ LUẬN" : "TRẮC NGHIỆM"}
              </Box>
              <Typography className={classes.headerTitle}>
                KẾT QUẢ THI: {examInfo?.examTitle || "Đang tải..."}
              </Typography>
              <Box className={classes.headerMeta}>
                <Chip
                  label={`Mã đề: #${params.id}`}
                  size="small"
                  className={classes.metaPill}
                />
                <Chip
                  icon={<TimerIcon style={{ color: "#A5B4FC", fontSize: 16 }} />}
                  label={`Thời gian: ${examInfo?.examTime || 0} phút`}
                  size="small"
                  className={classes.metaPill}
                />
                <Chip
                  icon={<StarIcon style={{ color: "#FBBF24", fontSize: 16 }} />}
                  label={`Tổng điểm đề: ${examInfo?.totalPoint || 100} điểm`}
                  size="small"
                  className={classes.metaPill}
                />
                {examInfo?.startDate && (
                  <Chip
                    icon={<EventIcon style={{ color: "#6EE7B7", fontSize: 16 }} />}
                    label={`Ngày thi: ${moment(examInfo.startDate).format("HH:mm - DD/MM/YYYY")}`}
                    size="small"
                    className={classes.metaPill}
                  />
                )}
              </Box>
            </Box>

            <Button
              className={classes.actionBtn}
              startIcon={<VisibilityIcon style={{ fontSize: 18 }} />}
              onClick={() => navigate(`/admin/manager-test/exam/${params.id}`)}
            >
              Xem cấu trúc đề thi
            </Button>
          </Box>
        </Box>

        {/* KPI Summary Cards */}
        <Grid container spacing={isMobile ? 1 : 2} className={classes.kpiGrid}>
          <Grid item xs={6} sm={6} md={3}>
            <Paper className={classes.kpiCard} elevation={0}>
              <Box className={classes.kpiIconBox} style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)", color: "#4F46E5" }}>
                <PeopleAltIcon />
              </Box>
              <Box>
                <Typography className={classes.kpiValue}>{totalAssigned}</Typography>
                <Typography className={classes.kpiLabel}>Tổng HV phân công</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Paper className={classes.kpiCard} elevation={0}>
              <Box className={classes.kpiIconBox} style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", color: "#059669" }}>
                <AssignmentTurnedInIcon />
              </Box>
              <Box>
                <Typography className={classes.kpiValue}>
                  {totalSubmitted} <span style={{ fontSize: "0.85rem", color: "#059669", fontWeight: 700 }}>({submissionRate}%)</span>
                </Typography>
                <Typography className={classes.kpiLabel}>Đã làm & nộp bài</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Paper className={classes.kpiCard} elevation={0}>
              <Box className={classes.kpiIconBox} style={{ background: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)", color: "#DC2626" }}>
                <CancelScheduleSendIcon />
              </Box>
              <Box>
                <Typography className={classes.kpiValue}>{totalSkipped}</Typography>
                <Typography className={classes.kpiLabel}>Học viên bỏ thi</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Paper className={classes.kpiCard} elevation={0}>
              <Box className={classes.kpiIconBox} style={{ background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)", color: "#D97706" }}>
                <AssessmentIcon />
              </Box>
              <Box>
                <Typography className={classes.kpiValue}>{avgScore}</Typography>
                <Typography className={classes.kpiLabel}>Điểm trung bình</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabbed Views */}
        <Paper className={classes.tabsPaper} elevation={0}>
          <Box className={classes.tabsHeader}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant={isMobile ? "fullWidth" : "standard"}
            >
              <Tab
                className={classes.tabItem}
                label={
                  <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    alignItems="center"
                    justifyContent="center"
                    style={{ gap: isMobile ? 2 : 6 }}
                  >
                    <PeopleAltIcon style={{ fontSize: isMobile ? 18 : 18 }} />
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <span>Tất cả</span>
                      <span className={classes.countBadge} style={{ backgroundColor: "#EEF2FF", color: "#4F46E5" }}>
                        {totalAssigned}
                      </span>
                    </Box>
                  </Box>
                }
              />
              <Tab
                className={classes.tabItem}
                label={
                  <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    alignItems="center"
                    justifyContent="center"
                    style={{ gap: isMobile ? 2 : 6 }}
                  >
                    <AssignmentTurnedInIcon style={{ fontSize: isMobile ? 18 : 18 }} />
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <span>Đã nộp</span>
                      <span className={classes.countBadge} style={{ backgroundColor: "#ECFDF5", color: "#059669" }}>
                        {totalSubmitted}
                      </span>
                    </Box>
                  </Box>
                }
              />
              <Tab
                className={classes.tabItem}
                label={
                  <Box
                    display="flex"
                    flexDirection={isMobile ? "column" : "row"}
                    alignItems="center"
                    justifyContent="center"
                    style={{ gap: isMobile ? 2 : 6 }}
                  >
                    <CancelScheduleSendIcon style={{ fontSize: isMobile ? 18 : 18 }} />
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <span>Bỏ thi</span>
                      <span className={classes.countBadge} style={{ backgroundColor: "#FEF2F2", color: "#DC2626" }}>
                        {totalSkipped}
                      </span>
                    </Box>
                  </Box>
                }
              />
            </Tabs>
          </Box>
        </Paper>

        <Box className={classes.tabContent}>
          {activeTab === 0 && <ListTestByCodeExam hideHeader mode="all" />}
          {activeTab === 1 && <ListTestByCodeExam hideHeader mode="submitted" />}
          {activeTab === 2 && <ListSkipExam hideHeader />}
        </Box>
      </Container>
    </div>
  );
}
